import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';

import {
    type ColumnProductLightFragment,
    type ColumnProductsFilterInput,
    ProductFlow,
    ProductState,
} from 'data/__generated__';
import { textColumn, multiSelectColumn, numberColumn } from 'components/DataTable';
import { DetailLink } from 'components/DetailsView';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { renderAsPrice } from 'helpers/price';
import { StatusTag } from 'pages/Products/components/StatusTag';
import { PRODUCT_FLOW_LABELS } from 'pages/Products/constants/flows';
import { PRODUCTS_STATES_LABELS } from 'pages/Products/constants/states';
import { siteVariationsColors, siteVariationsLabels } from '../constants';

export const getColumnStocksColumns = (): ColumnDef<ColumnProductLightFragment, unknown>[] => [
    textColumn<ColumnProductLightFragment, ColumnProductsFilterInput>({
        id: 'fullname',
        accessorFn: (row) => row.product.fullname,
        header: 'Produit',
        filterKey: 'searchValue',
        defaultVisible: true,
        enableServerSort: true,
        cell: ({ row }) => (
            <DetailLink
                name={row.original.product.fullname.slice(0, 60)}
                value={row.original.product._id}
                path="product"
            />
        ),
    }),
    multiSelectColumn<ColumnProductLightFragment, ColumnProductsFilterInput>({
        id: 'state',
        accessorFn: (row) => row.product.state,
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(ProductState).map((state) => ({
            value: state,
            label: PRODUCTS_STATES_LABELS[state] || state,
        })),
        renderOption: (option) => <StatusTag state={option.value as ProductState} />,
        cell: ({ row }) => <StatusTag state={row.original.product.state} />,
    }),
    multiSelectColumn<ColumnProductLightFragment, ColumnProductsFilterInput>({
        id: 'flow',
        accessorFn: (row) => row.product.flow,
        header: 'Flux',
        filterKey: 'flow',
        defaultVisible: false,
        enableServerSort: true,
        options: Object.values(ProductFlow).map((flow) => ({
            value: flow,
            label: PRODUCT_FLOW_LABELS[flow] || flow,
        })),
        cell: ({ row }) => <span>{PRODUCT_FLOW_LABELS[row.original.product.flow] ?? row.original.product.flow}</span>,
    }),
    {
        id: 'variations',
        header: 'Variations de stock',
        enableSorting: false,
        cell: ({ row }) => {
            const { latestStockVariations, lastColumnInventoryStock, stock } = row.original;
            if (latestStockVariations.length === 0) return <span>—</span>;

            let value = lastColumnInventoryStock?.value ?? stock;

            return (
                <TooltipProvider delayDuration={100}>
                    <VariationsContainer>
                        {latestStockVariations.map((variation) => {
                            value = value + variation.delta;
                            const valeur = value;
                            const bgColor = siteVariationsColors[variation.type];

                            return (
                                <Tooltip key={variation._id}>
                                    <TooltipTrigger asChild>
                                        <VariationBadge $bgColor={bgColor} data-test="stock-variation-badge">
                                            {variation.delta}
                                        </VariationBadge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <VariationDetails>
                                            <dt>Jour de création :</dt>
                                            <dd>{new Date(variation.createdAt).toLocaleDateString()}</dd>
                                            <dt>Heure de création :</dt>
                                            <dd>{new Date(variation.createdAt).toLocaleTimeString()}</dd>
                                            <dt>Type :</dt>
                                            <dd>{siteVariationsLabels[variation.type]}</dd>
                                            <dt>Valeur :</dt>
                                            <dd>{valeur}</dd>
                                            <dt>Variation :</dt>
                                            <dd>{variation.delta}</dd>
                                            <dt>Explication :</dt>
                                            <dd>{variation.explanation ?? '-'}</dd>
                                            <dt>Référence :</dt>
                                            <dd>{variation.ref ?? '-'}</dd>
                                        </VariationDetails>
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </VariationsContainer>
                </TooltipProvider>
            );
        },
    } as ColumnDef<ColumnProductLightFragment, unknown>,
    numberColumn<ColumnProductLightFragment, Record<string, unknown>>({
        id: 'stock',
        accessorFn: (row) => row.stock,
        header: 'Stock actuel',
        defaultVisible: true,
        enableServerSort: true,
        cell: ({ row }) => <span>{row.original.stock}</span>,
    }),
    numberColumn<ColumnProductLightFragment, Record<string, unknown>>({
        id: 'targetStock',
        accessorFn: (row) => row.targetStock,
        header: 'Stock cible',
        defaultVisible: true,
        cell: ({ row }) => <span>{row.original.targetStock}</span>,
    }),
    {
        id: 'availableStock',
        header: 'Stock disponible actuel',
        accessorFn: (row: ColumnProductLightFragment) =>
            row.availableStock.isUnlimited ? Infinity : row.availableStock.value,
        cell: ({ row }) => (
            <span>{row.original.availableStock.isUnlimited ? '∞' : row.original.availableStock.value}</span>
        ),
        meta: { defaultVisible: true },
    } as ColumnDef<ColumnProductLightFragment, unknown>,
    {
        id: 'merchantValuation',
        header: 'Valorisation marchande (en €HT)',
        accessorFn: (row: ColumnProductLightFragment) => row.merchantValuation,
        cell: ({ row }) => <span>{renderAsPrice(row.original.merchantValuation)}</span>,
        meta: { defaultVisible: true },
    } as ColumnDef<ColumnProductLightFragment, unknown>,
    numberColumn<ColumnProductLightFragment, Record<string, unknown>>({
        id: 'lastInventoryStock',
        accessorFn: (row) => row.lastColumnInventoryStock?.value ?? null,
        header: 'Stock du dernier inventaire',
        defaultVisible: true,
        cell: ({ row }) => <span>{row.original.lastColumnInventoryStock?.value ?? '-'}</span>,
    }),
    textColumn<ColumnProductLightFragment, Record<string, unknown>>({
        id: 'lastInventoryDate',
        accessorFn: (row) => row.lastColumnInventoryStock?.createdAt ?? null,
        header: 'Date du dernier inventaire',
        defaultVisible: true,
        cell: ({ row }) =>
            row.original.lastColumnInventoryStock ? (
                <span>{new Date(row.original.lastColumnInventoryStock.createdAt).toLocaleDateString()}</span>
            ) : (
                <span>-</span>
            ),
    }),
];

const VariationsContainer = styled.div`
    display: inline-block;
    overflow: auto;
    max-width: 150px;
`;

const VariationDetails = styled.dl`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 8px;
    margin: 0;

    dt {
        font-weight: 600;
        white-space: nowrap;
    }

    dd {
        margin: 0;
        word-break: break-word;
    }
`;

const VariationBadge = styled.span<{ $bgColor?: string }>`
    cursor: pointer;
    margin-right: 10px;
    background-color: ${({ $bgColor }) => $bgColor};
    color: ${({ $bgColor }) => ($bgColor === '#FFFFFF' ? '#111111' : '#FFFFFF')};
    border-radius: 3px;
    padding: 5px;
`;
